import React, { useCallback, useEffect, useMemo, useState } from "react";

const emptyItem = () => ({
  item_name: "",
  description: "",
  quantity: 1,
  unit_price: 0,
  discount_percent: 0,
  tax_percent: 0,
});

const normalizeNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const QuoteForm = ({
  accounts,
  contacts,
  opportunities,
  onSubmit,
  saving,
  prefillOpportunityId = "",
  defaultTerms = "",
  defaultNotes = "",
}) => {
  const [accountId, setAccountId] = useState("");
  const [contactId, setContactId] = useState("");
  const [opportunityId, setOpportunityId] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    if (!notes && defaultNotes) {
      setNotes(defaultNotes);
    }
  }, [defaultNotes, notes]);

  useEffect(() => {
    if (!terms && defaultTerms) {
      setTerms(defaultTerms);
    }
  }, [defaultTerms, terms]);

  useEffect(() => {
    if (!prefillOpportunityId) {
      return;
    }

    const foundOpportunity = opportunities.find(
      (row) => String(row.id) === String(prefillOpportunityId)
    );

    if (!foundOpportunity) {
      return;
    }

    setOpportunityId(foundOpportunity.id || "");
    setAccountId(foundOpportunity.account_id || "");
    setContactId(foundOpportunity.primary_contact_id || "");
  }, [prefillOpportunityId, opportunities]);

  const totals = useMemo(() => {
    return items.reduce(
      (accumulator, item) => {
        const quantity = normalizeNumber(item.quantity);
        const unitPrice = normalizeNumber(item.unit_price);
        const discountPercent = normalizeNumber(item.discount_percent);
        const taxPercent = normalizeNumber(item.tax_percent);

        const gross = quantity * unitPrice;
        const discountAmount = gross * (discountPercent / 100);
        const taxableBase = gross - discountAmount;
        const taxAmount = taxableBase * (taxPercent / 100);

        return {
          subtotal: accumulator.subtotal + gross,
          discountTotal: accumulator.discountTotal + discountAmount,
          taxTotal: accumulator.taxTotal + taxAmount,
          total: accumulator.total + taxableBase + taxAmount,
        };
      },
      { subtotal: 0, discountTotal: 0, taxTotal: 0, total: 0 }
    );
  }, [items]);

  const handleItemChange = useCallback((index, field, value) => {
    setItems((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  }, []);

  const handleAddItem = useCallback(() => {
    setItems((previous) => [...previous, emptyItem()]);
  }, []);

  const handleRemoveItem = useCallback((index) => {
    setItems((previous) =>
      previous.filter((_, itemIndex) => itemIndex !== index)
    );
  }, []);

  const handleSubmit = useCallback(() => {
    const validItems = items.filter((item) => item.item_name.trim());

    if (!accountId || validItems.length === 0) {
      return;
    }

    onSubmit({
      accountId,
      contactId,
      opportunityId,
      issueDate,
      validUntil,
      notes,
      terms,
      items: validItems,
    });
  }, [
    accountId,
    contactId,
    opportunityId,
    issueDate,
    validUntil,
    notes,
    terms,
    items,
    onSubmit,
  ]);

  return (
    <div className="crm-table-card">
      <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
        Create Quote
      </h3>

      <div className="crm-form-row">
        <div className="crm-form-group">
          <label>Account</label>
          <select
            className="crm-select"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            <option value="">Select account</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>

        <div className="crm-form-group">
          <label>Contact</label>
          <select
            className="crm-select"
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
          >
            <option value="">Select contact</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.full_name || "Unnamed contact"}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="crm-form-row">
        <div className="crm-form-group">
          <label>Opportunity</label>
          <select
            className="crm-select"
            value={opportunityId}
            onChange={(event) => setOpportunityId(event.target.value)}
          >
            <option value="">Select opportunity</option>
            {opportunities.map((opportunity) => (
              <option key={opportunity.id} value={opportunity.id}>
                {opportunity.name}
              </option>
            ))}
          </select>
        </div>

        <div className="crm-form-group">
          <label>Issue Date</label>
          <input
            className="crm-input"
            type="date"
            value={issueDate}
            onChange={(event) => setIssueDate(event.target.value)}
          />
        </div>
      </div>

      <div className="crm-form-row">
        <div className="crm-form-group">
          <label>Valid Until</label>
          <input
            className="crm-input"
            type="date"
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
          />
        </div>

        <div className="crm-form-group">
          <label>Terms</label>
          <input
            className="crm-input"
            value={terms}
            onChange={(event) => setTerms(event.target.value)}
            placeholder="Payment terms"
          />
        </div>
      </div>

      <div className="crm-form-group" style={{ marginBottom: "14px" }}>
        <label>Notes</label>
        <textarea
          className="crm-textarea"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Optional notes"
        />
      </div>

      <div style={{ marginBottom: "18px" }}>
        {items.map((item, index) => (
          <div
            key={`quote-item-${index}`}
            className="crm-table-card"
            style={{ marginBottom: "12px", padding: "14px" }}
          >
            <div className="crm-form-row">
              <div className="crm-form-group">
                <label>Item Name</label>
                <input
                  className="crm-input"
                  value={item.item_name}
                  onChange={(event) =>
                    handleItemChange(index, "item_name", event.target.value)
                  }
                />
              </div>

              <div className="crm-form-group">
                <label>Description</label>
                <input
                  className="crm-input"
                  value={item.description}
                  onChange={(event) =>
                    handleItemChange(index, "description", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label>Quantity</label>
                <input
                  className="crm-input"
                  type="number"
                  value={item.quantity}
                  onChange={(event) =>
                    handleItemChange(index, "quantity", event.target.value)
                  }
                />
              </div>

              <div className="crm-form-group">
                <label>Unit Price</label>
                <input
                  className="crm-input"
                  type="number"
                  value={item.unit_price}
                  onChange={(event) =>
                    handleItemChange(index, "unit_price", event.target.value)
                  }
                />
              </div>
            </div>

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label>Discount %</label>
                <input
                  className="crm-input"
                  type="number"
                  value={item.discount_percent}
                  onChange={(event) =>
                    handleItemChange(
                      index,
                      "discount_percent",
                      event.target.value
                    )
                  }
                />
              </div>

              <div className="crm-form-group">
                <label>Tax %</label>
                <input
                  className="crm-input"
                  type="number"
                  value={item.tax_percent}
                  onChange={(event) =>
                    handleItemChange(index, "tax_percent", event.target.value)
                  }
                />
              </div>
            </div>

            {items.length > 1 ? (
              <button
                className="crm-small-btn alt"
                onClick={() => handleRemoveItem(index)}
              >
                Remove Item
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <div className="crm-inline-actions" style={{ marginBottom: "16px" }}>
        <button className="crm-secondary-btn" onClick={handleAddItem}>
          Add Line Item
        </button>
      </div>

      <div className="crm-grid" style={{ marginBottom: "18px" }}>
        <div className="crm-kpi-card">
          <p className="crm-kpi-title">Subtotal</p>
          <h3 className="crm-kpi-value">₦{totals.subtotal.toLocaleString()}</h3>
        </div>
        <div className="crm-kpi-card">
          <p className="crm-kpi-title">Discount</p>
          <h3 className="crm-kpi-value">
            ₦{totals.discountTotal.toLocaleString()}
          </h3>
        </div>
        <div className="crm-kpi-card">
          <p className="crm-kpi-title">Tax</p>
          <h3 className="crm-kpi-value">₦{totals.taxTotal.toLocaleString()}</h3>
        </div>
        <div className="crm-kpi-card">
          <p className="crm-kpi-title">Total</p>
          <h3 className="crm-kpi-value">₦{totals.total.toLocaleString()}</h3>
        </div>
      </div>

      <button
        className="crm-primary-btn"
        onClick={handleSubmit}
        disabled={saving}
      >
        {saving ? "Saving..." : "Create Quote"}
      </button>
    </div>
  );
};

export default QuoteForm;
